import { Box, MenuItem, TextField, Typography, useTheme } from '@mui/material'
import { C100HistorySummary } from '../../../_types/carnivore100.type'
import { c100Tokens, scrollbarSx } from '../c100-tokens'
import { fmtPublishDate } from '../../../pages/carnivore100/carnivore100.history.util'

const MONO = "'IBM Plex Mono', monospace"

interface Props {
  versions: C100HistorySummary[] // newest first
  selected: number | null
  onSelect: (version: number) => void
}

// Every published list, newest first. A rail on desktop; a select on phones,
// where a column of dates would push the list itself below the fold.
export default function VersionRail({ versions, selected, onSelect }: Props) {
  const theme = useTheme()
  const t = c100Tokens(theme)

  return (
    <>
      <TextField
        select
        size="small"
        label="Published list"
        value={versions.some(v => v.version === selected) ? selected : ''}
        onChange={e => onSelect(Number(e.target.value))}
        sx={{
          display: { xs: 'flex', md: 'none' },
          mb: 2,
          '& .MuiOutlinedInput-root': { bgcolor: t.elevated, fontFamily: MONO, fontSize: 13 }
        }}
      >
        {versions.map(v => (
          <MenuItem key={v.version} value={v.version} sx={{ fontFamily: MONO, fontSize: 13 }}>
            {fmtPublishDate(v.publishedAt)}
            {v.isCurrent ? ' · Current' : ''}
          </MenuItem>
        ))}
      </TextField>

      <Box
        component="nav"
        aria-label="Published lists"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: 220,
          flexShrink: 0,
          bgcolor: t.card,
          border: `1px solid ${t.hairline}`,
          borderRadius: 2,
          maxHeight: 'calc(100vh - 160px)',
          overflowY: 'auto',
          position: 'sticky',
          top: 16,
          alignSelf: 'flex-start',
          ...scrollbarSx(t)
        }}
      >
        <Typography
          sx={{
            fontFamily: MONO,
            fontSize: 10,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: t.muted,
            px: 2,
            pt: 1.75,
            pb: 1
          }}
        >
          Published lists
        </Typography>
        {versions.map(v => {
          const active = v.version === selected
          return (
            <Box
              key={v.version}
              component="button"
              aria-current={active ? 'true' : undefined}
              onClick={() => onSelect(v.version)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                border: 'none',
                borderTop: `1px solid ${t.hairline}`,
                bgcolor: active ? t.faintAccentBg : 'transparent',
                boxShadow: active ? `inset 3px 0 0 ${t.accent}` : 'none',
                px: 2,
                py: 1.25,
                transition: 'background-color .15s',
                '&:hover': { bgcolor: t.faintAccentBg },
                '&:focus-visible': { outline: `2px solid ${t.accent}`, outlineOffset: -2 }
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: 13,
                    fontWeight: active ? 600 : 500,
                    color: t.text,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {fmtPublishDate(v.publishedAt)}
                </Typography>
                {v.isCurrent && (
                  <Typography
                    sx={{
                      fontFamily: MONO,
                      fontSize: 9.5,
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      color: t.accent,
                      textTransform: 'uppercase'
                    }}
                  >
                    Current
                  </Typography>
                )}
              </Box>
              {v.addedCount !== null && (
                <Typography
                  aria-label={`${v.addedCount} added, ${v.droppedCount} dropped`}
                  sx={{
                    fontFamily: MONO,
                    fontSize: 10.5,
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                >
                  <Box component="span" sx={{ color: t.gain }}>+{v.addedCount}</Box>
                  <Box component="span" sx={{ color: t.muted }}> / </Box>
                  <Box component="span" sx={{ color: t.loss }}>−{v.droppedCount}</Box>
                </Typography>
              )}
            </Box>
          )
        })}
      </Box>
    </>
  )
}
